import axios from 'axios';

// Surchargeable par REACT_APP_API_URL : le serveur de dev n'est pas toujours
// sur 8000 (un autre projet peut l'occuper).
const RACINE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/';

const axiosInstance = axios.create({
  baseURL: RACINE,
  headers: {
    'Content-Type': 'application/json',
  },
});

/** Les points d'entrée qu'on ne tente jamais de rejouer après un jeton refait. */
const SANS_JETON = ['token/', 'token/refresh/', 'signup/', 'password_reset/'];

/**
 * Renvoie l'utilisateur vers la connexion, en gardant où il allait.
 *
 * L'intercepteur se contentait de rejeter : chaque écran affichait alors son
 * propre « impossible de charger les données », sans que personne ne comprenne
 * que la session avait simplement expiré.
 */
function versLaConnexion() {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  if (window.location.pathname === '/login') return;
  const retour = window.location.pathname + window.location.search;
  window.location.href = `/login?expire=1&suite=${encodeURIComponent(retour)}`;
}

// Un seul rafraîchissement à la fois : un écran qui lance trois appels reçoit
// trois 401, et trois rafraîchissements concurrents se marcheraient dessus.
let rafraichissement = null;

function rafraichirLeJeton() {
  if (rafraichissement) return rafraichissement;

  const refresh = localStorage.getItem('refresh_token');
  // « undefined » : ce que l'ancien code écrivait quand la réponse ne portait
  // pas de nouveau jeton de rafraîchissement.
  if (!refresh || refresh === 'undefined' || refresh === 'null') {
    return Promise.reject(new Error('sans jeton de rafraîchissement'));
  }

  rafraichissement = axios
    .post(`${RACINE.replace(/\/$/, '')}/token/refresh/`, { refresh })
    .then(({ data }) => {
      localStorage.setItem('access_token', data.access);
      // La rotation des jetons est désactivée côté serveur : la réponse ne
      // porte que `access`. Écrire `data.refresh` y déposait la chaîne
      // « undefined », et le rafraîchissement suivant échouait pour toujours.
      if (data.refresh) {
        localStorage.setItem('refresh_token', data.refresh);
      }
      return data.access;
    })
    .finally(() => { rafraichissement = null; });

  return rafraichissement;
}

axiosInstance.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem('access_token');
    if (accessToken && accessToken !== 'undefined') {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const requete = error.config;
    const url = requete?.url || '';
    const authentification = SANS_JETON.some((chemin) => url.includes(chemin));

    if (error.response?.status !== 401 || requete?._rejoue || authentification) {
      return Promise.reject(error);
    }

    requete._rejoue = true;
    try {
      const jeton = await rafraichirLeJeton();
      requete.headers = { ...requete.headers, Authorization: `Bearer ${jeton}` };
      return await axiosInstance(requete);
    } catch (echec) {
      // La session est bel et bien finie : on le dit, au lieu de laisser
      // chaque écran annoncer une panne de chargement.
      versLaConnexion();
      return Promise.reject(echec);
    }
  },
);

export default axiosInstance;
